import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Skeleton } from "antd";
import { DetailList, PageHeader, SectionCard, StatusTag, useFlatPage } from "../components/ui";
import notify from "../utils/notify";

const { TextArea } = Input;

// the fields the API accepts when saving the hotel (location fields are carried over untouched)
const DTO_FIELDS = [
  "hotelName",
  "address",
  "contactNumber",
  "email",
  "website",
  "socialMediaHandle",
  "mapCoordinates",
  "physicalLocation",
  "townOrVillage",
  "district",
  "province",
  "country",
  "description",
  "numberOfRooms",
  "type",
  "amenities",
  "activities",
];

const asList = (v) => (Array.isArray(v) ? v : []);

const HotelProfilePage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [hotel, setHotel] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!hotelId) {
      setError("Hotel ID is missing.");
      setLoading(false);
      return;
    }

    const fetchHotel = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/Hotel/${hotelId}`);
        setHotel(res.data);
        setError("");
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load the hotel.");
      } finally {
        setLoading(false);
      }
    };

    fetchHotel();
  }, [hotelId]);

  const openEdit = () => {
    form.setFieldsValue({
      hotelName: hotel?.hotelName,
      type: hotel?.type,
      numberOfRooms: hotel?.numberOfRooms,
      description: hotel?.description,
      address: hotel?.address,
      contactNumber: hotel?.contactNumber,
      email: hotel?.email,
      website: hotel?.website,
      amenities: asList(hotel?.amenities),
      activities: asList(hotel?.activities),
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const dto = {};
      DTO_FIELDS.forEach((k) => {
        dto[k] = values[k] !== undefined ? values[k] : hotel?.[k];
      });

      setSaving(true);
      const res = await axiosInstance.put(`/Hotel/${hotelId}`, dto);
      setHotel(res.data);
      setEditOpen(false);
      notify.success("Hotel details updated");
    } catch (e) {
      if (e?.errorFields) return; // form validation
      notify.error(e.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hms-page">
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      </div>
    );
  }

  if (error && !hotel) {
    return (
      <div className="hms-page">
        <Alert type="error" showIcon title="Could not load the hotel" description={error} />
      </div>
    );
  }

  const location = [
    { label: "Physical location", value: hotel?.physicalLocation },
    { label: "Town or village", value: hotel?.townOrVillage },
    { label: "District", value: hotel?.district },
    { label: "Province", value: hotel?.province },
    { label: "Country", value: hotel?.country },
    { label: "Map coordinates", value: hotel?.mapCoordinates },
    { label: "Social media", value: hotel?.socialMediaHandle },
  ].filter((x) => x.value);

  const chips = (list, empty) =>
    asList(list).length ? (
      <span>
        {asList(list).map((x) => (
          <StatusTag key={x} status="confirmed">
            {x}
          </StatusTag>
        ))}
      </span>
    ) : (
      <span className="hms-muted">{empty}</span>
    );

  return (
    <div className="hms-page hp-page">
      <PageHeader
        actions={
          <Button type="primary" onClick={openEdit}>
            Edit hotel info
          </Button>
        }
      />

      <div className="hms-stack">
        <SectionCard>
          <div className="hms-summary" style={{ marginBottom: 4 }}>
            <div>
              <div className="hms-summary-title">{hotel?.hotelName || "Hotel"}</div>
              <div className="hms-summary-sub">{[hotel?.type, hotel?.numberOfRooms ? `${hotel.numberOfRooms} rooms` : null].filter(Boolean).join(" · ") || "No type set"}</div>
            </div>
          </div>
          {hotel?.description && <p style={{ margin: "8px 0 0", color: "var(--hms-text-sub)" }}>{hotel.description}</p>}
        </SectionCard>

        <div className="hms-grid-2">
          <SectionCard title="Contact">
            <DetailList
              items={[
                { label: "Address", value: hotel?.address },
                { label: "Phone", value: hotel?.contactNumber },
                { label: "Email", value: hotel?.email ? <a href={`mailto:${hotel.email}`}>{hotel.email}</a> : null },
                {
                  label: "Website",
                  value: hotel?.website ? (
                    <a href={hotel.website} target="_blank" rel="noreferrer">
                      {hotel.website}
                    </a>
                  ) : null,
                },
              ]}
            />
          </SectionCard>

          <SectionCard title="Location">
            {location.length ? <DetailList items={location} /> : <div className="hms-empty">No location details yet</div>}
          </SectionCard>
        </div>

        <div className="hms-grid-2">
          <SectionCard title="Amenities">{chips(hotel?.amenities, "No amenities listed")}</SectionCard>
          <SectionCard title="Activities">{chips(hotel?.activities, "No activities listed")}</SectionCard>
        </div>
      </div>

      <Modal
        title="Edit hotel info"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSave}
        okText="Save changes"
        confirmLoading={saving}
        width={680}
        forceRender
      >
        <Form form={form} layout="vertical">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Item name="hotelName" label="Hotel name" rules={[{ required: true, message: "Please enter the hotel name" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="type" label="Type">
              <Input placeholder="e.g. Lodge, Guest house, Hotel" />
            </Form.Item>
            <Form.Item name="numberOfRooms" label="Number of rooms">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="contactNumber" label="Phone">
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Enter a valid email" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input placeholder="https://" />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="amenities" label="Amenities" extra="Type a name and press Enter to add it.">
            <Select mode="tags" tokenSeparators={[","]} open={false} />
          </Form.Item>
          <Form.Item name="activities" label="Activities" extra="Type a name and press Enter to add it.">
            <Select mode="tags" tokenSeparators={[","]} open={false} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HotelProfilePage;
